import { useState, useEffect, useCallback } from "react";
import React from "react";
import { Container, Row, Col, Card, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import LeftNav from "../../common/leftnav";
import authService from "../../../services/authService";
import { useUser } from "../../../context/UserContext";
import BusinessDetails from "./business_details";
import CoreServices from "./core_services";
import BusinessHours from "./business_hours";
import Directions from "./directions";

const BusinessInfo = () => {
  const { token } = useUser();
  const [businessData, setBusinessData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  // Wrap the function in useCallback to prevent it from changing on every render
  const businessInfoData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await authService.getBusinessInfo(token);
      setBusinessData(response[0]);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  }, [token]); // Depend only on token
  useEffect(() => {
    if (token) {
      businessInfoData();
    }
  }, [token, businessInfoData]);

  const navigate = useNavigate();
  const handleUpdate = async (updatedData, type) => {
    try {
      setIsLoading(true);
      let newBusinessData = { ...businessData };

      // Update only the relevant field based on type
      if (type === "details") {
        newBusinessData = { ...newBusinessData, ...updatedData };
      } else if (type === "direction") {
        newBusinessData.direction = updatedData;
      } else if (type === "coreServices") {
        newBusinessData.coreServices = updatedData;
      } else if (type === "hours") {
        // Ensure businessHours is properly formatted
        newBusinessData.bussinessHours = updatedData.bussinessHours.map(
          (item) => ({
            day: item.day,
            hours: item.hours || "Closed", // Default to empty string if hours are missing
          })
        );

        newBusinessData.timezone = updatedData.timezonesInit;
      }

      // Call API immediately
      await authService.patchBusinessInfo(token, {
        name: newBusinessData?.name,
        website: newBusinessData?.website,
        address: newBusinessData?.address,
        country: newBusinessData?.country,
        bussinessOverview: newBusinessData?.overview,
        coreServices: newBusinessData?.coreServices,
        bussinessHours: newBusinessData?.bussinessHours,
        direction: newBusinessData?.direction,
        timezone: newBusinessData?.timezone,
      });
    } catch (error) {
      console.error("Error:", error);
    } finally {
      businessInfoData();
    }
  };
  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }
  return (
    <div className="d-flex">
      <LeftNav />
      <Container
        fluid
        className="instructions-content flex-grow-1 p-4 bg-light"
      >
        <Row>
          <Col>
            <h2 className="fs-five-title fw-bold black-color">
              Agent Settings
            </h2>
            <p className="gray-color fs-6">
              Customize Nouraa to enhance your business call experience.
            </p>
          </Col>
        </Row>
        <div className="rounded-3" style={{ backgroundColor: "#ffffff" }}>
          <Row>
            <Col>
              <Card
                className="px-4 pb-0 border-0"
                style={{ paddingTop: "1rem" }}
              >
                <div>
                  <Link
                    to="/agent_settings"
                    className="text-decoration-none purple-color fw-bold mb-4 fs-four"
                    onClick={() => navigate("/agent_settings")}
                  >
                    <img
                      src="../assets/arrow-left.svg"
                      alt="Arrow Right"
                      className="me-1"
                    />
                    Go Back
                  </Link>
                  <h2
                    className="fs-two fw-bold black-color"
                    style={{ marginTop: "12px", marginBottom: "12px" }}
                  >
                    <img
                      src="../assets/globe-blue.svg"
                      alt="Arrow Right"
                      className="me-2"
                    />
                    Business Info
                  </h2>
                  <p className="gray-color fs-four">
                    This business information gives Nouraa the context to handle
                    your calls.{" "}
                  </p>
                  <hr className="mb-4 border-dark-subtle" />
                </div>
              </Card>
            </Col>
          </Row>
          <div
            className="d-flex flex-column pb-4 black-color"
            style={{ gap: "32px" }}
          >
            <BusinessDetails
              onUpdate={(data) => handleUpdate(data, "details")}
              initialData={[
                businessData?.name,
                businessData?.website,
                businessData?.address,
                businessData?.country,
                businessData?.bussinessOverview,
              ]}
            />
            <CoreServices
              onUpdate={(data) => handleUpdate(data, "coreServices")}
              initialData={businessData?.coreServices}
            />
            <BusinessHours
              onUpdate={(data) => handleUpdate(data, "hours")}
              initialData={{
                businessHours: businessData?.bussinessHours,
                timezonesInit: businessData?.timezone,
              }}
            />

            <Directions
              onUpdate={(data) => handleUpdate(data, "direction")}
              initialData={businessData?.direction}
            />
          </div>
        </div>
      </Container>
    </div>
  );
};

export default BusinessInfo;
